import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { ResearchProject, KoboAccount } from '../types';
import { apiService } from '../services/api';

const NewProjectPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ 
    title: '', 
    description: '',
    koboAccountId: '' 
  });
  const [loading, setLoading] = useState(false);
  const [koboAccounts, setKoboAccounts] = useState<KoboAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const toast = useRef<Toast>(null);

  useEffect(() => {
    loadKoboAccounts();
  }, []);

  const loadKoboAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const response = await apiService.getUserProfile();
      
      if (response.success && response.data) {
        const accounts = response.data.kobo_accounts || [];
        setKoboAccounts(accounts);
        
        // Set default account if available
        const defaultAccount = response.data.default_kobo_account;
        if (defaultAccount) {
          setFormData(prev => ({ ...prev, koboAccountId: defaultAccount.account_id }));
        }
      }
    } catch (error) {
      console.error('Failed to load KoboToolbox accounts:', error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Validation Error', detail: 'Project title is required' });
      return;
    }

    if (!formData.koboAccountId) {
      toast.current?.show({ severity: 'warn', summary: 'Validation Error', detail: 'KoboToolbox account selection is required' });
      return;
    }

    try {
      setLoading(true);
      const projectData: Partial<ResearchProject> = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        status: 'draft',
      };
      
      const response = await apiService.createProject(projectData, formData.koboAccountId);
      
      if (response.success && response.data) {
        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Project created successfully' });
        navigate(`/app/projects/${response.data.projectId}`);
      } else {
        throw new Error(response.error || 'Failed to create project');
      }
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to create project' });
    } finally {
      setLoading(false);
    }
  };

  const cardStyle: React.CSSProperties = { backgroundColor: 'white', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' };

  return (
    <div>
      {/* Header Section */}
      <div style={{ padding: '2rem', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <h1 style={{ fontSize: '1.5rem', margin: 0, color: '#1f2937' }}>Create New Project</h1>
        <p style={{ color: '#6b7280', margin: '0.5rem 0 0 0' }}>Start a new research automation workflow</p>
      </div>

      {/* Main Content Section */}
      <div style={{ padding: '2rem', backgroundColor: '#f8f9fa', minHeight: 'calc(100vh - 140px)' }}>
        <div style={cardStyle}>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                Project Title *
              </label>
              <InputText
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter a descriptive project title"
                className="w-full"
                required
              />
              <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>Choose a clear, descriptive title</small>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                Description
              </label>
              <InputTextarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your research objectives and scope"
                rows={4}
                className="w-full"
              />
              <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>Optional: Research objectives, target population, expected outcomes</small>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                KoboToolbox Account *
              </label>
              {loadingAccounts ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px' }}>
                  <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.875rem' }}></i>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Loading accounts...</span>
                </div>
              ) : koboAccounts.length === 0 ? (
                <div style={{ padding: '0.75rem', border: '1px solid #ef4444', borderRadius: '6px', backgroundColor: '#fef2f2' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <i className="pi pi-exclamation-triangle" style={{ color: '#ef4444' }}></i>
                    <span style={{ fontSize: '0.875rem', color: '#ef4444', fontWeight: 500 }}>KoboToolbox account required</span>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: '#dc2626', margin: '0 0 0.5rem 0' }}>
                    You must configure a KoboToolbox account before creating projects. This is required for survey deployment.
                  </p>
                  <Button
                    label="Add KoboToolbox Account"
                    icon="pi pi-plus"
                    size="small"
                    severity="danger"
                    text
                    onClick={() => navigate('/app/profile')}
                  />
                </div>
              ) : (
                <>
                  <Dropdown
                    value={formData.koboAccountId}
                    options={koboAccounts.map(account => ({
                      label: `${account.account_name} (${account.account_info.username || account.account_info.email})`,
                      value: account.account_id
                    }))}
                    onChange={(e) => setFormData(prev => ({ ...prev, koboAccountId: e.value }))}
                    placeholder="Select a KoboToolbox account"
                    className="w-full"
                    required
                  />
                  <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                    Required: Select which KoboToolbox account to use for survey deployment
                  </small>
                </>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <Button type="submit" label="Create Project" icon="pi pi-check" loading={loading} />
              <Button type="button" label="Cancel" icon="pi pi-times" severity="secondary" onClick={() => navigate('/app/projects')} />
            </div>
          </form>

          {/* Info Box */}
          <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#dbeafe', borderRadius: '8px', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <i className="pi pi-info-circle" style={{ color: '#1e40af', marginTop: '0.125rem' }}></i>
            <div>
              <div style={{ fontWeight: 500, color: '#1e40af', marginBottom: '0.5rem' }}>Next Steps</div>
              <div style={{ fontSize: '0.875rem', color: '#1e40af' }}>
                After creating your project, you'll be guided through:
                <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem' }}>
                  <li>Upload Terms of Reference (TOR)</li>
                  <li>AI extraction of indicators</li>
                  <li>Automated questionnaire design</li>
                  <li>KoboToolbox deployment</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Toast ref={toast} />
    </div>
  );
};

export default NewProjectPage;
